// RejectedTable.jsx
import React, { useState } from "react";
import { ChevronDown, Download, ChevronLeft, ChevronRight, X } from "lucide-react";
import { formatDate, MappingStatus, MappingStyles } from "../../utils/utils";

export default function RejectedTable({
  selectedAll,
  selectedApplicants,
  handleSelectAll,
  filteredApplicants,
  handleSelectApplicant, // Ensure this is the one used
  handleViewApplication,
  getStatusClass,
}) {
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  // Parent component now sends paginated data, so we directly use filteredApplicants
  // Each 'distribution' in distributionsToRender is a LoanDistribution object
  const distributionsToRender = Array.isArray(filteredApplicants)
    ? filteredApplicants
    : [];

  const handleStatusClick = (rejectionReason) => {
    setSelectedReason(rejectionReason || "No rejection reason provided.");
    setShowReasonModal(true);
  };

  const handleCloseModal = () => {
    setShowReasonModal(false);
    setSelectedReason("");
  };
  return (
    <div className="bg-[#131313] rounded-lg overflow-hidden ">
      <div className="overflow-x-auto scrollbar-hide">
        <table className="min-w-full divide-y divide-gray-800 border-t border-[#333333]">
          <thead className=" text-white border-t border-[#333333]">
            <tr>
              <th className="p-4 text-center border-r  border-[#333333]">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
       before:content-['✔'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedAll}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="p-4 text-center border-r  border-[#333333]">
                APPLICATION ID
              </th>
              <th className="p-4 text-center border-r  border-[#333333]">
                APPLICANT NAME
              </th>
              <th className="p-4 text-center border-r  border-[#333333]">
                LOAN AMOUNT
              </th>
              <th className="p-4 text-center border-r  border-[#333333]">
                ASSIGNED DATE
              </th>
              <th className="p-4 text-center border-r  border-[#333333]">
                STATUS
              </th>
              <th className="p-4 text-center border-r  border-[#333333]">
                APPLICATION
              </th>
              <th className="p-4 text-center">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {(() => {
              if (distributionsToRender.length === 0) {
                return (
                  <tr>
                    <td
                      colSpan="8"
                      className="p-6 text-center text-gray-400 border-none"
                    >
                      {" "}
                      {/* Corrected colSpan */}
                      No rows found
                    </td>
                  </tr>
                );
              }
              return distributionsToRender.map((distribution, index) => (
                <tr
                  key={distribution._id || index} // Use distribution._id
                  className=" text-white border-t border-[#333333]"
                >
                  <td className="p-4 text-center border-r  border-[#333333]">
                    <input
                      type="checkbox"
                      className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                           before:content-['✔'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      checked={selectedApplicants.includes(distribution._id)}
                      onChange={() => handleSelectApplicant(distribution._id)}
                    />
                  </td>
                  <td className="p-4 text-center border-r  border-[#333333]">
                    <div>
                      <div>{distribution._id}</div> {/* Distribution ID */}
                      <div className="text-xs text-gray-400 mt-1">
                        {/* Displaying distribution's sentAt or createdAt */}
                        {formatDate(
                          distribution.sentAt || distribution.createdAt
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-center border-r  border-[#333333]">
                    {distribution.loan?.user?.name || "N/A"}{" "}
                    {/* Access nested property */}
                  </td>
                  <td className="p-4 text-center border-r  border-[#333333]">
                    <div>
                      <div>
                        ₹
                        {distribution.loan?.principle?.toLocaleString("en-IN") ||
                          "N/A"}{" "}
                        {/* Access nested property */}
                      </div>
                      <div className="text-sm text-gray-400 mt-1">
                        {distribution.loan?.loanType || "N/A"}{" "}
                        {/* Access nested property */}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-center border-r  border-[#333333]">
                    {/* Date when distribution was responded to or updated */}
                    {formatDate(distribution.updatedAt)}
                  </td>
                  <td className="p-4 text-center border-r  border-[#333333]">
                    {/* Status of the LoanDistribution */}
                    <span
                      className={`px-4 py-1 rounded-full text-sm cursor-pointer hover:opacity-80 transition-opacity ${getStatusClass(
                        distribution.status
                      )}`}
                      onClick={() => handleStatusClick(distribution.rejectionReason)}
                      title="Click to view rejection reason"
                    >
                      {distribution.status}
                    </span>
                  </td>

                  <td className="p-4 text-center border-r  border-[#333333]">
                    <button
                      className="text-yellow-500 hover:text-yellow-400"
                      onClick={() => handleViewApplication(distribution._id)} // Pass distributionId
                    >
                      View
                    </button>
                  </td>
                  <td>
                    <div className="flex items-center gap-2 justify-center">
                      <button>
                        <span className="text-yellow-500 hover:text-yellow-400">
                          Download
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {/* Rejection Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#131313] rounded-lg p-6 w-96 max-w-[90vw] border border-[#333333]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">Rejection Reason</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-white text-sm">
                {selectedReason || "No rejection reason provided."}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-[#FFCA20] text-black rounded-md hover:bg-yellow-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
